import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Building2, Plus, Loader2, RefreshCw, X, ExternalLink, Trash2, Pencil,
  CheckCircle2, Eye, EyeOff, Network, Users, ShieldAlert, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  'hospital', 'snf', 'alf', 'home_health_agency', 'hospice_agency',
  'physician_group', 'payer', 'aco', 'ipa', 'fqhc', 'imaging_center',
  'lab', 'dme_supplier', 'pharmacy', 'other',
];
const TIERS = ['preferred', 'standard', 'referral_only', 'pilot'];
const REGIONS = ['northeast', 'southeast', 'midwest', 'southwest', 'west', 'national'];

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: ShieldAlert, label: 'EVV Compliance', href: '/admin/evv' },
  { icon: Users, label: 'Clinician Retention', href: '/admin/clinician-retention' },
  { icon: Network, label: 'Partner Directory', active: true },
];

export default function AdminPartnerDirectory() {
  const { getAuthHeader } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/network/directory`, getAuthHeader());
      setPartners(data.partners || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load directory');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const togglePublished = async (p) => {
    try {
      await axios.patch(`${API}/admin/network/directory/${p.id}`, { published: !p.published }, getAuthHeader());
      toast.success(p.published ? 'Hidden from public directory' : 'Published to public directory');
      await load();
    } catch (e) { toast.error('Could not update'); }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive ${p.name}?`)) return;
    try {
      await axios.delete(`${API}/admin/network/directory/${p.id}`, getAuthHeader());
      toast.success('Partner archived');
      await load();
    } catch (e) { toast.error('Could not archive'); }
  };

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Partner Directory"
      subtitle="The public network catalog. Add hospitals, agencies, payers, and physician groups so case managers + procurement teams can verify partnerships in one place."
      testId="admin-partner-directory-page"
    >
      <div className="cn-card p-4 mb-6 flex items-center justify-between" data-testid="partner-admin-controls">
        <div className="text-sm text-cn-text-muted">{partners.length} partner{partners.length === 1 ? '' : 's'} · published partners appear at <code className="px-1 py-0.5 bg-cn-bone-100 border border-cn-border rounded text-xs">/partners</code></div>
        <div className="flex gap-2">
          <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="partner-admin-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={() => setShowCreate(true)} className="cn-btn cn-btn-primary !py-2" data-testid="partner-admin-create-btn">
            <Plus className="w-4 h-4" /> Add partner
          </Button>
        </div>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && partners.length === 0 && (
        <div className="cn-card p-12 text-center" data-testid="partner-admin-empty">
          <Building2 className="w-8 h-8 text-cn-text-muted mx-auto mb-2" />
          <p className="text-sm text-cn-text-muted">No partners yet. Click <strong>Add partner</strong> to seed the directory.</p>
        </div>
      )}

      {!loading && partners.length > 0 && (
        <div className="cn-card overflow-hidden" data-testid="partner-admin-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partner-admin-table">
              <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                <tr className="text-left">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Tier</th>
                  <th className="px-3 py-3">Location</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cn-border">
                {partners.map((p) => (
                  <tr key={p.id} className="hover:bg-cn-bone-50/40" data-testid={`partner-row-${p.id}`}>
                    <td className="px-5 py-2.5">
                      <p className="font-semibold text-cn-text">{p.name}</p>
                      <p className="text-[10px] text-cn-text-muted">slug: {p.slug}</p>
                    </td>
                    <td className="px-3 py-2.5 text-cn-text-muted text-xs">{p.partner_type}</td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-cn-bone-100 border border-cn-border text-cn-text">{p.tier}</span>
                    </td>
                    <td className="px-3 py-2.5 text-cn-text-muted text-xs">{[p.city, p.state, p.region].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${p.published ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-testid={`partner-status-${p.id}`}>
                        {p.published ? 'Published' : 'Hidden'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => togglePublished(p)} className="p-1.5 hover:bg-cn-bone-100 rounded text-cn-text-muted hover:text-cn-text" title={p.published ? 'Hide' : 'Publish'} data-testid={`partner-toggle-${p.id}`}>
                          {p.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-cn-bone-100 rounded text-cn-text-muted hover:text-cn-primary" title="Edit" data-testid={`partner-edit-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <a href={`/partners/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-cn-bone-100 rounded text-cn-text-muted hover:text-cn-text" title="View public page">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button onClick={() => archive(p)} className="p-1.5 hover:bg-cn-bone-100 rounded text-cn-text-muted hover:text-red-600" title="Archive" data-testid={`partner-archive-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(showCreate || editing) && (
        <PartnerForm
          initial={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={async () => { setShowCreate(false); setEditing(null); await load(); }}
        />
      )}
    </DashboardShell>
  );
}

function PartnerForm({ initial, onClose, onSaved }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    partner_type: initial?.partner_type || 'hospital',
    tier: initial?.tier || 'standard',
    region: initial?.region || '',
    city: initial?.city || '',
    state: initial?.state || '',
    description: initial?.description || '',
    specialties: (initial?.specialties || []).join(', '),
    logo_url: initial?.logo_url || '',
    website_url: initial?.website_url || '',
    intake_url: initial?.intake_url || '',
    contact_email: initial?.contact_email || '',
    quote: initial?.quote || '',
    quote_attribution: initial?.quote_attribution || '',
    published: initial ? Boolean(initial.published) : true,
  }));
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || form.name.length < 2) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        specialties: form.specialties.split(',').map((s) => s.trim()).filter(Boolean),
      };
      // Strip empty strings (the backend treats omitted optional fields as null)
      Object.keys(payload).forEach((k) => {
        if (typeof payload[k] === 'string' && payload[k].trim() === '') delete payload[k];
      });
      if (initial) {
        await axios.patch(`${API}/admin/network/directory/${initial.id}`, payload, getAuthHeader());
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/admin/network/directory`, payload, getAuthHeader());
        toast.success('Partner added');
      }
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="partner-form-modal">
      <div className="cn-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-lg text-cn-text">{initial ? 'Edit partner' : 'Add partner'}</h3>
          <button onClick={onClose} className="text-cn-text-muted hover:text-cn-text"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <FormRow label="Organization name *">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-form-name" />
          </FormRow>
          <div className="grid sm:grid-cols-3 gap-3">
            <FormRow label="Type">
              <select value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })} className="w-full border border-cn-border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="partner-form-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormRow>
            <FormRow label="Tier">
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full border border-cn-border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="partner-form-tier">
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </FormRow>
            <FormRow label="Region">
              <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="w-full border border-cn-border rounded-lg px-3 py-2 mt-1 text-sm" data-testid="partner-form-region">
                <option value="">—</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormRow>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormRow label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="partner-form-city" />
            </FormRow>
            <FormRow label="State (2-letter)">
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} data-testid="partner-form-state" />
            </FormRow>
          </div>
          <FormRow label="Description">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full border border-cn-border rounded-lg px-3 py-2 text-sm" placeholder="700-bed level-1 trauma center; runs the regional stroke + cardiac discharge pathways on CaraLoom." data-testid="partner-form-description" />
          </FormRow>
          <FormRow label="Specialties (comma-separated)">
            <Input value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} placeholder="trauma, cardiac, stroke" data-testid="partner-form-specialties" />
          </FormRow>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormRow label="Website URL"><Input value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://" /></FormRow>
            <FormRow label="Intake URL"><Input value={form.intake_url} onChange={(e) => setForm({ ...form, intake_url: e.target.value })} placeholder="https://" /></FormRow>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <FormRow label="Contact email"><Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></FormRow>
            <FormRow label="Logo URL"><Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://" /></FormRow>
          </div>
          <FormRow label='Testimonial (optional)'>
            <textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={2} className="w-full border border-cn-border rounded-lg px-3 py-2 text-sm" />
          </FormRow>
          <FormRow label="Testimonial attribution">
            <Input value={form.quote_attribution} onChange={(e) => setForm({ ...form, quote_attribution: e.target.value })} placeholder="Jane Doe, VP Care Operations" />
          </FormRow>
          <label className="flex items-center gap-2 text-sm text-cn-text" data-testid="partner-form-published-label">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="partner-form-published" />
            Publish to public directory
          </label>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <Button onClick={onClose} className="cn-btn cn-btn-ghost">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="cn-btn cn-btn-primary" data-testid="partner-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {initial ? 'Update partner' : 'Add partner'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-cn-text-muted">{label}</label>
      {children}
    </div>
  );
}
